/* eslint-disable */
// @ts-nocheck

import React, { Suspense, lazy } from 'react';
import TagManager from 'react-gtm-module';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/system';
import { TrackJS } from 'trackjs';
import { RecoilRoot } from 'recoil';

import { PrivateRoutes } from './feature/PrivateRoutes';
import Login from './pages/Login';
import MainLayout from './layout/MainLayout';
import { jpCRM } from './styles/MuiTheme';
import { CaseBoardProvider } from './contexts/caseBoard';
import { googleTagManagerId, trackJSToken } from './constants/general';
import Loading from './components/Loading';
import ErrorTest from './pages/ErrorTest';
import ErrorBoundary from './pages/Error';

const CaseList = lazy(() => import('./pages/CaseList'));
const CaseBoard = lazy(() => import('./pages/CaseBoard'));
const AddNewCase = lazy(() => import('./pages/AddNewCase'));
const AssignedList = lazy(() => import('./pages/CaseList/AssignedList'));
const FollowUpList = lazy(() => import('./pages/CaseList/FollowUpList'));
const SharedList = lazy(() => import('./pages/CaseList/SharedList'));
const CasesByGroup = lazy(() => import('./pages/Cases/ByGroup'));
const Cases = lazy(() => import('./pages/Cases'));
const CasesByIntakeManager = lazy(() => import('./pages/Cases/ByIntakeManager'));
const CasesByAll = lazy(() => import('./pages/Cases/AllCases'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const ViewNotes = lazy(() => import('./pages/CaseBoard/ViewNotes'));
const ViewCommunication = lazy(() => import('./pages/CaseBoard/ViewCommunication'));
const ViewSummary = lazy(() => import('./pages/CaseBoard/ViewSummary'));
const ViewReferral = lazy(() => import('./pages/CaseBoard/ViewReferral'));
const ViewFiles = lazy(() => import('./pages/CaseBoard/ViewFiles'));
const ViewDocusign = lazy(() => import('./pages/CaseBoard/ViewDocusign'));
const ReferralList = lazy(() => import('./pages/CaseList/ReferralList'));
const DashboardsList = lazy(() => import('./pages/Dashboards'));
const Dashboard = lazy(() => import('./pages/Dashboards/Dashboard'));
const CampaignsList = lazy(() => import('./pages/Campaigns'));
const Campaign = lazy(() => import('./pages/Campaigns/Campaign'));
const AssignmentList = lazy(() => import('./pages/Assignments'));
const IMTracker = lazy(() => import('./pages/IMTracker'));
const ViewLegalReview = lazy(() => import('./pages/CaseBoard/LegalOps/ViewLegalReview'));

function App() {
    if (!TrackJS.isInstalled()) {
        TrackJS.install({
            token: trackJSToken,
            application: 'crm'
        });
    }

    const tagManagerArgs = {
        gtmId: googleTagManagerId
    };

    TagManager.initialize(tagManagerArgs);

    return (
        <RecoilRoot>
            <ThemeProvider theme={jpCRM}>
                <CaseBoardProvider>
                    <ErrorBoundary>
                        <BrowserRouter>
                            <Routes>
                                <Route element={<PrivateRoutes />}>
                                    <Route path="/" element={<MainLayout />}>
                                        <Route index element={<Navigate to="/case-list/assigned-cases?page=0" />} />
                                        <Route
                                            path="/case-list"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <CaseList />
                                                </Suspense>
                                            }
                                        >
                                            <Route
                                                path="follow-up"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <FollowUpList />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="shared-cases"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <SharedList />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="assigned-cases"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <AssignedList />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="referral-list"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ReferralList />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="search-results"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <SearchResults />
                                                    </Suspense>
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="/case-board/:caseId"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <CaseBoard />
                                                </Suspense>
                                            }
                                        >
                                            <Route
                                                path="notes"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ViewNotes />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="summary"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ViewSummary />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="communication"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ViewCommunication />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="referral"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ViewReferral />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="sol"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ViewLegalReview />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="docusign"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ViewDocusign />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="files"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ViewFiles />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="legal-ops/review"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <ViewLegalReview />
                                                    </Suspense>
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="add-new-case"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <AddNewCase />
                                                </Suspense>
                                            }
                                        />
                                        <Route
                                            path="/cases"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <Cases />
                                                </Suspense>
                                            }
                                        >
                                            <Route index element={<Navigate to="/cases/all" />} />
                                            <Route
                                                path="group/:groupNumber"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <CasesByGroup />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="id/:id"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <CasesByIntakeManager />
                                                    </Suspense>
                                                }
                                            />
                                            <Route
                                                path="all"
                                                element={
                                                    <Suspense fallback={<Loading />}>
                                                        <CasesByAll />
                                                    </Suspense>
                                                }
                                            />
                                        </Route>
                                        <Route
                                            path="/dashboards/all"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <DashboardsList />
                                                </Suspense>
                                            }
                                        />
                                        <Route
                                            path="/dashboards/all/:name"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <Dashboard />
                                                </Suspense>
                                            }
                                        />
                                        <Route
                                            path="/im-tracker"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <IMTracker />
                                                </Suspense>
                                            }
                                        />
                                        <Route
                                            path="/campaigns/all"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <CampaignsList />
                                                </Suspense>
                                            }
                                        />
                                        <Route
                                            path="/campaigns/all/:name"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <Campaign />
                                                </Suspense>
                                            }
                                        />
                                        <Route
                                            path="/assignments"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <AssignmentList />
                                                </Suspense>
                                            }
                                        />
                                        <Route
                                            path="/errortest"
                                            element={
                                                <Suspense fallback={<Loading />}>
                                                    <ErrorTest />
                                                </Suspense>
                                            }
                                        />
                                    </Route>
                                </Route>
                                <Route path="/login" element={<Login />} />
                            </Routes>
                        </BrowserRouter>
                    </ErrorBoundary>
                </CaseBoardProvider>
            </ThemeProvider>
        </RecoilRoot>
    );
}

export default App;
